<template>
  <div class="whatsapp-syncing">
    <v-row align="stretch">
      <v-col cols="12" lg="8">
        <v-card rounded="lg" elevation="0" class="pa-6 h-100">
          <h2 class="text-h5 font-weight-bold mb-4">
            {{ $t('settings.whatsapp.syncing.title') }}
          </h2>

          <div class="d-flex flex-wrap align-center gap-2 mb-6">
            <span class="text-h6">
              {{ displayNumber }}
            </span>
            <span
              v-if="verifiedName"
              class="text-body-1 text-medium-emphasis"
            >
              · {{ verifiedName }}
            </span>
            <v-chip
              v-if="messagingEnabled"
              color="success"
              size="small"
              variant="flat"
              class="ml-2"
            >
              {{ $t('settings.whatsapp.states.connected') }}
            </v-chip>
            <!-- TODO: re-enable when history sync is reliable
            <v-chip
              color="amber-darken-2"
              size="small"
              variant="flat"
              class="ml-2"
            >
              {{ $t('settings.whatsapp.syncing.chipSyncing') }}
            </v-chip>
            -->
          </div>

          <!-- TODO: re-enable when history sync is reliable
          <v-list lines="two" class="bg-transparent pa-0">
            <v-list-item class="px-0">
              <template #prepend>
                <v-icon color="success" class="mr-2">
                  mdi-check-circle
                </v-icon>
              </template>
              <v-list-item-title class="font-weight-medium">
                {{ $t('settings.whatsapp.syncing.stepConnected') }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item class="px-0">
              <template #prepend>
                <v-icon
                  :color="contactsDone ? 'success' : 'grey'"
                  class="mr-2"
                >
                  {{ contactsDone ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
              </template>
              <v-list-item-title>
                {{ contactsStepLabel }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item class="px-0">
              <template #prepend>
                <v-icon
                  :color="historyDone ? 'success' : 'grey'"
                  class="mr-2"
                >
                  {{ historyDone ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
              </template>
              <v-list-item-title class="mb-2">
                {{ historyStepLabel }}
              </v-list-item-title>
              <v-progress-linear
                :model-value="historyProgress"
                color="red-darken-3"
                height="8"
                rounded
              />
              <p class="text-caption text-medium-emphasis mt-1 mb-0">
                {{ historyProgress }}%
              </p>
            </v-list-item>

            <v-list-item class="px-0">
              <template #prepend>
                <v-icon
                  :color="assistantReady ? 'success' : 'grey-lighten-1'"
                  class="mr-2"
                >
                  {{ assistantReady ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
              </template>
              <v-list-item-title :class="{ 'text-medium-emphasis': !assistantReady }">
                {{ $t('settings.whatsapp.syncing.stepAssistant') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
          -->

          <v-alert
            v-if="limitReached"
            type="info"
            variant="tonal"
            class="mt-4 mb-0"
            density="comfortable"
          >
            {{ $t('settings.whatsapp.syncing.syncLimitReached') }}
          </v-alert>

          <v-alert
            v-if="notRequested"
            type="warning"
            variant="tonal"
            class="mt-4 mb-0"
            density="comfortable"
          >
            {{ $t('settings.whatsapp.syncing.syncNotRequested', { reason: notRequestedReason }) }}
          </v-alert>

          <div v-if="isStaff && showRetrySync" class="mt-6">
            <v-btn
              variant="outlined"
              color="red-darken-3"
              :loading="syncLoading"
              :disabled="syncLoading"
              @click="onRetrySync"
            >
              {{ $t('settings.whatsapp.syncing.retrySync') }}
            </v-btn>
            <p v-if="syncStatusLabel" class="text-caption text-medium-emphasis mt-2 mb-0">
              {{ syncStatusLabel }}
            </p>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card rounded="lg" elevation="0" class="pa-6 h-100">
          <h3 class="text-h6 font-weight-bold mb-3">
            {{ $t('settings.whatsapp.syncing.sideTitle') }}
          </h3>
          <p class="text-body-2 mb-4">
            {{ $t('settings.whatsapp.syncing.sideLead') }}
          </p>
          <ul class="syncing-notes pl-4 mb-0">
            <li
              v-for="(key, idx) in noteKeys"
              :key="idx"
              class="text-body-2 text-medium-emphasis mb-2"
            >
              {{ $t(key) }}
            </li>
          </ul>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { requestWhatsappSync } from '@/api/whatsapp';
import { whatsappErrorMessage } from '@/utils/whatsappErrors';

const SYNC_RETRY_WINDOW_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'WhatsappSyncing',
  props: {
    whatsapp: {
      type: Object,
      required: true,
    },
    institutionId: {
      type: String,
      default: '',
    },
    isStaff: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['reload', 'snackbar'],
  data() {
    return {
      syncLoading: false,
      noteKeys: [
        'settings.whatsapp.syncing.noteWeb',
        'settings.whatsapp.syncing.noteGroups',
        'settings.whatsapp.syncing.noteBroadcast',
      ],
    };
  },
  computed: {
    displayNumber() {
      return (this.whatsapp && this.whatsapp.display_phone_number)
        || '—';
    },
    verifiedName() {
      return (this.whatsapp && this.whatsapp.verified_name) || '';
    },
    // Token is never sent to the portal; mode + phone_number_id imply token_enc exists.
    messagingEnabled() {
      const w = this.whatsapp;
      if (!w || !w.phone_number_id) {
        return false;
      }
      if (w.mode === 'none' || !w.mode) {
        return false;
      }
      const status = w.status;
      if (status === 'disconnected' || status === 'error' || status === 'not_connected') {
        return false;
      }
      return status === 'active' || status === 'syncing' || status === 'pending';
    },
    historyProgress() {
      const raw = this.whatsapp && this.whatsapp.sync_progress;
      const n = Number(raw);
      if (Number.isNaN(n)) {
        return 0;
      }
      return Math.min(100, Math.max(0, Math.round(n)));
    },
    contactsImported() {
      return Boolean(this.whatsapp && this.whatsapp.contacts_imported);
    },
    contactsDone() {
      return this.contactsImported;
    },
    contactsStepLabel() {
      const w = this.whatsapp;
      if (this.contactsImported) {
        return this.$t('settings.whatsapp.syncing.stepContacts');
      }
      if (w && (w.sync_requested_at || w.sync_status === 'requested' || w.sync_status === 'already_requested')) {
        return this.$t('settings.whatsapp.syncing.stepContactsStarted');
      }
      if (w && w.contacts_sync_request_id) {
        return this.$t('settings.whatsapp.syncing.stepContactsStarted');
      }
      return this.$t('settings.whatsapp.syncing.stepContactsPending');
    },
    historyDone() {
      return Boolean(this.whatsapp && this.whatsapp.history_shared)
        || this.historyProgress >= 100;
    },
    historyStepLabel() {
      if (this.historyDone) {
        return this.$t('settings.whatsapp.syncing.stepHistoryDone');
      }
      return this.$t('settings.whatsapp.syncing.stepHistory');
    },
    limitReached() {
      return Boolean(this.whatsapp && this.whatsapp.sync_status === 'limit_reached');
    },
    assistantReady() {
      const w = this.whatsapp;
      if (!w) {
        return false;
      }
      if (w.status === 'active') {
        return true;
      }
      if (this.limitReached && w.phone_number_id) {
        return true;
      }
      return this.historyDone && this.contactsDone;
    },
    connectedAtMs() {
      if (!this.whatsapp || !this.whatsapp.connected_at) {
        return null;
      }
      return new Date(this.whatsapp.connected_at).getTime();
    },
    notRequested() {
      return Boolean(this.whatsapp && this.whatsapp.sync_status === 'not_requested');
    },
    notRequestedReason() {
      return (this.whatsapp && this.whatsapp.sync_not_requested_reason) || '—';
    },
    // Server applies the retry rules (status, Meta limit, 24h window); mirror the window
    // client-side so the button hides without a reload.
    showRetrySync() {
      if (!this.institutionId || !this.whatsapp || !this.whatsapp.sync_retry_allowed) {
        return false;
      }
      return Boolean(this.connectedAtMs)
        && Date.now() - this.connectedAtMs <= SYNC_RETRY_WINDOW_MS;
    },
    syncStatusLabel() {
      const status = this.whatsapp && this.whatsapp.sync_status;
      if (status === 'incomplete') {
        return this.$t('settings.whatsapp.syncing.syncIncomplete');
      }
      if (status === 'already_requested') {
        return this.$t('settings.whatsapp.syncing.syncAlreadyRequested');
      }
      if (status === 'failed') {
        return this.$t('settings.whatsapp.syncing.syncFailedHint');
      }
      if (status === 'limit_reached') {
        return this.$t('settings.whatsapp.syncing.syncLimitReached');
      }
      return '';
    },
  },
  methods: {
    async onRetrySync() {
      if (!this.institutionId || this.syncLoading) {
        return;
      }
      this.syncLoading = true;
      try {
        await requestWhatsappSync(this.institutionId);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.syncing.retrySyncOk'),
          color: 'success',
        });
        this.$emit('reload');
      } catch (err) {
        this.$emit('snackbar', {
          message: whatsappErrorMessage(err),
          color: 'error',
        });
      } finally {
        this.syncLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.syncing-notes {
  list-style-type: disc;
}
</style>
